<template>
  <section class="n-section-secondary" :style="`background-color: var(--c-${vars.backgroundColor})`">
    <div v-if="variants.v1 === variant" class="tw-flex tw-w-full tw-flex-col lg:tw-flex-row lg:tw-justify-center">
      <div
        v-for="(count, index) in counts"
        :key="`countsection-count-${index}`"
        class="tw-flex tw-flex-col tw-items-center tw-justify-start tw-break-all tw-py-6 tw-text-center md:tw-py-10 lg:tw-w-full lg:tw-py-20 2xl:tw-py-24"
        ref="countElements"
      >
        <NuxtImg v-if="count.icon" :src="count.icon" class="tw-mb-7 tw-w-16" />
        <div class="tw-flex tw-flex-row tw-items-center tw-text-[var(--c-primary)]">
          <h3 v-if="count.prefix">{{ count.prefix }}</h3>
          <h3 class="odometer tw-whitespace-nowrap">0</h3>
          <h3 v-if="count.suffix">{{ count.suffix }}</h3>
        </div>
        <p class="tw-mx-6 tw-min-h-[48px]">{{ count.description }}</p>
      </div>
    </div>

    <div v-if="variants.v2 === variant" class="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center">
      <h2 class="tw-mb-3">{{ vars.titleText }}</h2>
      <div class="tw-flex tw-w-full tw-flex-col lg:tw-flex-row lg:tw-justify-center">
        <div
          v-for="(count, index) in counts"
          :key="`countsection-count-${index}`"
          class="tw-flex tw-flex-col tw-items-center tw-justify-start tw-break-all tw-py-6 tw-text-center md:tw-py-10 lg:tw-w-full lg:tw-py-20 2xl:tw-py-24"
          ref="countElements"
        >
          <NuxtImg v-if="count.icon" :src="count.icon" class="tw-mb-7 tw-w-16" />
          <div class="tw-flex tw-flex-row tw-items-center tw-text-[var(--c-primary)]">
            <h3 v-if="count.prefix">{{ count.prefix }}</h3>
            <h3 class="odometer tw-whitespace-nowrap">0</h3>
            <h3 v-if="count.suffix">{{ count.suffix }}</h3>
          </div>
          <p class="tw-mx-6 tw-min-h-[48px]">{{ count.description }}</p>
        </div>
      </div>
    </div>

 
<div
  v-if="variants.v3 === variant"
  class="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center"
>
  <h6 class="tw-mb-3">{{ vars.titleText }}</h6>
  <div class="tw-flex tw-w-full tw-flex-col lg:tw-flex-row lg:tw-justify-center">
    <div
      v-for="(count, index) in counts"
      :key="`countsection-count-${index}`"
      class="tw-flex tw-flex-col tw-items-center tw-justify-start tw-break-all tw-py-6 tw-text-center md:tw-py-10 lg:tw-w-full lg:tw-py-20 2xl:tw-py-24"
      ref="countElements"
    >
      <NuxtImg v-if="count.icon" :src="count.icon" class="tw-mb-7 tw-w-16" />
      <div class="tw-flex tw-flex-row tw-items-center tw-text-[var(--c-primary)]">
        <h1 v-if="count.prefix">{{ count.prefix }}</h1>
       
        <h1 class="js-count">0</h1>
        <h1 v-if="count.suffix">{{ count.suffix }}</h1>
      </div>
      <h6 class="tw-mx-6 tw-min-h-[48px]">{{ count.description }}</h6>
    </div>
  </div>
</div>

  </section>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';

export default defineNuxtComponent({
  name: 'CountSection',
  mixins: [ComponentMixin],

  data() {
    return {
      variants: {
        v1: 'v1',
        v2: 'v2',
        v3: 'v3',
      },
      observers: [] as IntersectionObserver[],
      animatedElements: new Set<number>(),
    };
  },

  computed: {
    counts() {
      return this.groupedVariables.counts;
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.setupIntersectionObservers();
    });
  },

  beforeUnmount() {
    this.observers.forEach((observer) => observer.disconnect());
  },

  methods: {
    setupIntersectionObservers() {
      const options = { root: null, rootMargin: '0px', threshold: 0.5 };
      const elements = this.$refs.countElements;
      if (!elements) return;

      (elements as Element[]).forEach((element, index) => {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.animatedElements.has(index)) {
              this.updateCount(index);
              this.animatedElements.add(index);
              observer.unobserve(entry.target);
            }
          });
        }, options);

        observer.observe(element);
        this.observers.push(observer);
      });
    },

   
    parseNumericString(raw: unknown): { value: number; decimals: number } {
      const str = String(raw ?? '').replace(/\s+/g, '');
      const m = str.match(/-?\d+(?:[.,]\d+)?/);
      if (!m) return { value: 0, decimals: 0 };

      let numStr = m[0];

      if (numStr.includes('.') && numStr.includes(',')) {
        numStr = numStr.replace(/,/g, '');
      } else if (numStr.includes(',')) {
       
        numStr = numStr.replace(',', '.');
      }

      const decimals = numStr.includes('.') ? (numStr.split('.')[1] || '').length : 0;
      const value = parseFloat(numStr);
      if (Number.isNaN(value)) return { value: 0, decimals: 0 };
      return { value, decimals };
    },

    updateCount(index: number) {
      const count = this.counts[index];
      const container = (this.$refs.countElements as Element[])[index];

  
      const jsCountEl = container.querySelector('.js-count') as HTMLElement | null;
      const odoEl = container.querySelector('.odometer') as HTMLElement | null;

     
      if (jsCountEl) {
        const { value: endValue, decimals } = this.parseNumericString(count?.title);
        const duration = 2000; 
        const stepTime = 50;   
        const steps = Math.max(1, Math.floor(duration / stepTime));
        const increment = (endValue - 0) / steps;

        const format = (v: number) =>
          decimals > 0 ? v.toFixed(decimals) : Math.floor(v).toString();

        let current = 0;
        jsCountEl.innerText = decimals > 0 ? (0).toFixed(decimals) : '0';

        const tick = () => {
          current += increment;
          const done =
            (increment >= 0 && current >= endValue) ||
            (increment < 0 && current <= endValue);

          if (!done) {
            jsCountEl.innerText = format(current);
            requestAnimationFrame(() => setTimeout(tick, stepTime));
          } else {
            jsCountEl.innerText = decimals > 0 ? endValue.toFixed(decimals) : Math.floor(endValue).toString();
          }
        };

        tick();
        return;
      }

  
      if (odoEl) {
       
        const endValue = parseInt(String(count?.title).replace(/[^\d-]/g, ''), 10) || 0;
        const duration = 2000;
        const stepTime = 50;
        const steps = Math.max(1, Math.floor(duration / stepTime));
        const increment = (endValue - 0) / steps;

        let current = 0;
        odoEl.innerText = '0';

        const tick = () => {
          current += increment;
          const done =
            (increment >= 0 && current >= endValue) ||
            (increment < 0 && current <= endValue);

          if (!done) {
            odoEl.innerText = Math.floor(current).toString();
            requestAnimationFrame(() => setTimeout(tick, stepTime));
          } else {
            odoEl.innerText = endValue.toString();
          }
        };

        tick();
      }
    },
  },
});
</script>



